import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Empty, ErrorBlock, Img, Input, Spinner } from "../components/ui.tsx";
import { ImageUpload } from "../components/image-upload.tsx";
import { useSpaces } from "../lib/site-config.ts";
import { api } from "../api/client.ts";
import { toCents } from "../api/org-client.ts";
import { cn } from "@/lib/utils";

/**
 * ═══════════════════════════════════════════════════════════════
 *  后台 · 新增 / 编辑装修案例
 * ═══════════════════════════════════════════════════════════════
 *
 * 老板：「装修案例的这个设计我觉得很不错的，但是我没有找到任何一个地方
 * 新增装修案例」。
 *
 * 🔴 **接口（POST / PATCH / DELETE）早就齐了，只有界面一直没做** ——
 *    后台那一页只有「发布 / 下线」两个动作，等于案例只能靠种子脚本造。
 *    这是这个项目里反复出现的一种状态：能力在，但没有入口 = 不存在。
 *
 * 🔴 **案例只存 `productIds`，绝不存价格。**
 *    这个平台里"商品价格"不是商品的属性，是「商品 × 看它的人」算出来的结果。
 *    把价格随内容存死的话，同一篇案例对所有人都是一个数 ——
 *    而正确的行为是：游客看吊牌价，挂在王师傅名下的客户看王师傅给的价。
 *    所以这张表单里**没有任何价格输入框**，只有"挑哪几件商品"。
 */

export interface AdminCase {
  id: number;
  slug: string;
  title: string;
  style: string;
  spaces?: string[];
  areaSqm?: number | null;
  budget?: number | null;
  coverKey?: string | null;
  contentMd?: string;
  imageKeys?: string[];
  productIds: number[];
  viewCount: number;
  published: boolean;
  sortOrder?: number;
}

/*
  ⚠️ 空间词表统一从后台配置取（`useSpaces`）—— 以前这里写死四个、漏了阳台，
     给案例打标签时根本选不到那一格。
*/
interface Draft {
  id: number | null;
  slug: string;
  title: string;
  style: string;
  spaces: string[];
  areaSqm: string;
  budget: string;
  coverKeys: string[];
  contentMd: string;
  imageKeys: string[];
  productIds: number[];
  published: boolean;
  sortOrder: string;
}

export const EMPTY_CASE: Draft = {
  id: null,
  slug: "",
  title: "",
  style: "",
  spaces: [],
  areaSqm: "",
  budget: "",
  coverKeys: [],
  contentMd: "",
  imageKeys: [],
  productIds: [],
  published: false,
  sortOrder: "0",
};

/** 标题 → slug 的兜底：中文取不出来，就给一个时间戳。 */
function slugify(title: string): string {
  const ascii = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return ascii.length >= 2 ? ascii.slice(0, 60) : `case-${Date.now().toString(36)}`;
}

export function CaseEditor({
  open,
  editing,
  onClose,
  orgId,
}: {
  open: boolean;
  /** null = 新增。 */
  editing: AdminCase | null;
  onClose: () => void;
  /**
   * 传图时标注归属（可选）。
   * ⚠️ 后台不传 —— 平台后台上传的图本来就是平台的，媒体接口这个参数
   *    只是给商家端标"这张图是谁传的"，不传不影响上传。
   */
  orgId?: number;
}) {
  const qc = useQueryClient();
  const spaces = useSpaces();
  const [d, setD] = useState<Draft>(EMPTY_CASE);

  /**
   * 编辑时要拿**完整**的一条 —— 列表出参里没有正文和图。
   * ⚠️ 直接拿列表那条来填表单的话，正文和图是空的，一保存就把它们清了。
   */
  const full = useQuery({
    queryKey: ["admin", "case", editing?.id],
    queryFn: () => api.get<AdminCase>(`/admin/cases/${editing!.id}`),
    enabled: open && Boolean(editing),
  });

  useEffect(() => {
    if (!open) return;
    if (!editing) {
      setD(EMPTY_CASE);
      return;
    }
    const c = full.data;
    if (!c) return;
    setD({
      id: c.id,
      slug: c.slug,
      title: c.title,
      style: c.style ?? "",
      spaces: c.spaces ?? [],
      areaSqm: c.areaSqm == null ? "" : String(c.areaSqm),
      /** ⚠️ 库里存的是**分**，界面上填的是元。 */
      budget: c.budget == null ? "" : (c.budget / 100).toFixed(2),
      coverKeys: c.coverKey ? [c.coverKey] : [],
      contentMd: c.contentMd ?? "",
      imageKeys: c.imageKeys ?? [],
      productIds: c.productIds ?? [],
      published: c.published,
      sortOrder: String(c.sortOrder ?? 0),
    });
  }, [open, editing, full.data]);

  /** 挑商品的候选。⚠️ 只出在架的 —— 引用一件下架商品，前台会静默跳过。 */
  const products = useQuery({
    queryKey: ["admin", "case-products"],
    queryFn: () =>
      api.get<{ items: { id: number; title: string; imageUrls: string[] }[] }>(
        "/products?sort=sales&limit=60",
      ),
    enabled: open,
  });

  const [kw, setKw] = useState("");
  const rows = useMemo(() => {
    const items = products.data?.items ?? [];
    const k = kw.trim();
    return k ? items.filter((p) => p.title.includes(k)) : items;
  }, [products.data, kw]);

  /**
   * 🔴 **发布与否要当参数传，不能先 `setD` 再 `mutate`。**
   *    React 的 state 更新是异步的 —— 那样写的话 `mutate` 里读到的还是**旧值**，
   *    表现是"点了「保存并发布」却存成草稿"，而且时灵时不灵。
   */
  const save = useMutation({
    mutationFn: (published: boolean) => {
      const body = {
        slug: d.slug.trim() || slugify(d.title),
        title: d.title.trim(),
        style: d.style.trim() || undefined,
        spaces: d.spaces,
        areaSqm: d.areaSqm.trim() ? Number(d.areaSqm) : null,
        budget: d.budget.trim() ? toCents(d.budget) : null,
        coverKey: d.coverKeys[0],
        contentMd: d.contentMd,
        imageKeys: d.imageKeys,
        productIds: d.productIds,
        published,
        sortOrder: Number(d.sortOrder) || 0,
      };
      return d.id === null
        ? api.post("/admin/cases", body)
        : api.patch(`/admin/cases/${d.id}`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "cases"] });
      /** 🔴 前台那份也要作废 —— 刚发布的案例要立刻在商城里看得到。 */
      qc.invalidateQueries({ queryKey: ["cases"] });
      onClose();
    },
  });

  const toggleProduct = (id: number) =>
    setD((s) => ({
      ...s,
      productIds: s.productIds.includes(id)
        ? s.productIds.filter((x) => x !== id)
        : [...s.productIds, id],
    }));

  const ready = d.title.trim().length > 0 && d.coverKeys.length > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-[1000px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{d.id === null ? "新增装修案例" : "编辑案例"}</DialogTitle>
          <DialogDescription>
            案例把商品放进一个具体的家里 —— 建材是低频高客单，用户往往不知道
            "我家该买什么"，一篇案例比一页商品列表更容易让人下决心。
          </DialogDescription>
        </DialogHeader>

        {editing && full.isPending ? (
          <Spinner compact />
        ) : (
          /* ⚠️ 两栏：左边内容、右边挑商品。窄成一栏的话挑商品要滚很久。 */
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <Input
                label="标题"
                placeholder="例如：南山 89㎡ 三口之家：奶油风全屋建材清单"
                value={d.title}
                onChange={(e) => setD({ ...d, title: e.target.value })}
              />

              <div>
                <span className="mb-1.5 block text-[13px] text-muted-foreground">
                  封面图（必填）
                </span>
                {/*
                  ⚠️ 封面是列表页那张大图 —— 没有封面的案例在列表里就是一个灰块，
                     所以这里是必填（下面的「保存」会禁用）。
                */}
                <ImageUpload
                  value={d.coverKeys}
                  onChange={(v) => setD({ ...d, coverKeys: v.slice(-1) })}
                  scope="product"
                  orgId={orgId}
                  max={1}
                  label="上传封面"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input
                  label="风格"
                  placeholder="奶油风 / 侘寂风 / 现代简约"
                  value={d.style}
                  onChange={(e) => setD({ ...d, style: e.target.value })}
                />
                <Input
                  label="面积（㎡）"
                  inputMode="decimal"
                  value={d.areaSqm}
                  onChange={(e) => setD({ ...d, areaSqm: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/*
                  ⚠️ 这是**这套房主材总共花了多少**（内容的一部分），
                     跟商品价格是两回事 —— 商品价格永远按访问者现算，
                     案例里一个价都不存。
                */}
                <Input
                  label="装修预算（元）"
                  inputMode="decimal"
                  placeholder="12800"
                  value={d.budget}
                  onChange={(e) => setD({ ...d, budget: e.target.value })}
                />
                <Input
                  label="排序（越大越靠前）"
                  inputMode="numeric"
                  value={d.sortOrder}
                  onChange={(e) => setD({ ...d, sortOrder: e.target.value })}
                />
              </div>

              <div>
                <span className="mb-1.5 block text-[13px] text-muted-foreground">
                  适用空间（前台按它筛选）
                </span>
                <div className="flex flex-wrap gap-2">
                  {spaces.map((sp) => {
                    const on = d.spaces.includes(sp.key);
                    return (
                      <button
                        key={sp.key}
                        type="button"
                        onClick={() =>
                          setD({
                            ...d,
                            spaces: on
                              ? d.spaces.filter((x) => x !== sp.key)
                              : [...d.spaces, sp.key],
                          })
                        }
                        className={cn(
                          "rounded-lg border px-3 py-1.5 text-[13px] transition-colors",
                          on
                            ? "border-brand-500 bg-brand-50 text-brand-700"
                            : "border-border text-muted-foreground hover:bg-secondary",
                        )}
                      >
                        {sp.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <span className="mb-1.5 block text-[13px] text-muted-foreground">正文</span>
                <textarea
                  rows={8}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-[14px] leading-relaxed outline-none focus:border-brand-500"
                  placeholder={"业主是一对夫妻加一个上小学的孩子。预算有限，把钱花在了每天要摸到的地方："}
                  value={d.contentMd}
                  onChange={(e) => setD({ ...d, contentMd: e.target.value })}
                />
              </div>

              <div>
                <span className="mb-1.5 block text-[13px] text-muted-foreground">
                  内容配图（正文下面那几张）
                </span>
                <ImageUpload
                  value={d.imageKeys}
                  onChange={(v) => setD({ ...d, imageKeys: v })}
                  scope="product"
                  orgId={orgId}
                  max={20}
                  label="添加配图"
                  variant="strip"
                />
              </div>
            </div>

            {/* ── 右：挑商品 ── */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[13px] text-muted-foreground">
                  这套用到的建材（已选 {d.productIds.length} 件）
                </span>
              </div>
              {/*
                🔴 **只挑商品，不填价。** 案例只存 `productIds` ——
                   前台每次按访问者的归属链现算：同一篇案例，
                   游客看吊牌价，挂在某个分销商名下的客户看那个分销商给的价。
                   把价格存进内容里的话，这条就整个坏了。
              */}
              <Input
                className="mb-2 h-9"
                placeholder="搜商品名"
                value={kw}
                onChange={(e) => setKw(e.target.value)}
              />
              {products.isPending ? (
                <Spinner compact />
              ) : rows.length === 0 ? (
                <Empty title="没有匹配的商品" compact />
              ) : (
                <div className="grid max-h-[520px] grid-cols-2 gap-2 overflow-y-auto rounded-lg border border-border p-2 xl:grid-cols-3">
                  {rows.map((p) => {
                    const at = d.productIds.indexOf(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => toggleProduct(p.id)}
                        className={cn(
                          "relative overflow-hidden rounded-lg border text-left transition-colors",
                          at >= 0 ? "border-brand-500" : "border-border hover:border-brand-300",
                        )}
                      >
                        <Img
                          src={p.imageUrls?.[0]}
                          alt={p.title}
                          className="aspect-[4/3] w-full object-cover"
                        />
                        {/* 标出第几件 —— 顺序就是案例里的显示顺序。 */}
                        {at >= 0 && (
                          <span className="absolute top-1 left-1 flex size-5 items-center justify-center rounded-full bg-brand-600 text-[11px] text-white">
                            {at + 1}
                          </span>
                        )}
                        <span className="block truncate px-2 py-1.5 text-[12px]">{p.title}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {save.error && <ErrorBlock error={save.error} />}

        <DialogFooter>
          {/*
            ⚠️ 「存草稿」和「保存并发布」分开：写到一半存下来是常态，
               而发布是**面向所有访客**的动作，不该跟保存混成一个按钮。
          */}
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button
            variant="outline"
            disabled={!ready}
            loading={save.isPending && save.variables === false}
            onClick={() => save.mutate(false)}
          >
            存草稿
          </Button>
          <Button
            disabled={!ready}
            loading={save.isPending && save.variables === true}
            onClick={() => save.mutate(true)}
          >
            保存并发布
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
